import { useMemo } from 'react';

import {
  infiniteQueryOptions,
  QueryOptions,
  useInfiniteQuery,
} from '@tanstack/react-query';
import flatMap from 'lodash/flatMap';

import { apiClient } from '@cast/infrastructure-api';
import { useOrganizations } from '@cast/shared-organization-context-data-access';
import { ApiResponse, TimeRange } from '@cast/shared-types';
import { SortDirection } from '@cast/design-system';

const VALUE_REPORT_WORKLOADS_LIST_QUERY_KEY = 'value-report-workloads-list';

const getValueReportWorkloadsListQueryKey = ({
  clusterIds,
  key,
  useListingPrices,
  timeZone,
  range,
  organizationId,
  pageLimit,
  sortField,
  sortOrder,
  namespaces,
  workloadNames,
  kinds,
}: {
  clusterIds: string[];
  key: string;
  useListingPrices?: boolean;
  timeZone: string;
  range: TimeRange;
  organizationId: string | undefined;
  pageLimit?: number;
  sortField?: string;
  sortOrder?: SortDirection;
  namespaces: string[];
  workloadNames: string[];
  kinds: string[];
}) =>
  [
    VALUE_REPORT_WORKLOADS_LIST_QUERY_KEY,
    organizationId,
    clusterIds,
    key,
    useListingPrices,
    timeZone,
    range,
    pageLimit,
    sortField,
    sortOrder,
    namespaces,
    workloadNames,
    kinds,
  ] as const;

const apiFn = apiClient.costReport.getValueReportWorkloadsList;

type UseValueReportWorkloadsListQueryData = ApiResponse<typeof apiFn>;

type UseValueReportWorkloadsListQueryArgs<SELECT> = QueryOptions<
  UseValueReportWorkloadsListQueryData,
  SELECT
> & {
  pageLimit?: number;
  timeZone: string;
  clusterIds: string[];
  namespaces: string[];
  workloadNames: string[];
  kinds: string[];
  key: string;
  getRange: () => TimeRange;
  useListingPrices?: boolean;
  sortField?: string;
  sortOrder?: SortDirection;
  enabled?: boolean;
};

const getValueReportWorkloadsListQueryOptions = <
  SELECT = UseValueReportWorkloadsListQueryData
>(
  {
    clusterIds,
    key,
    getRange,
    timeZone,
    useListingPrices,
    pageLimit = 100,
    sortField,
    sortOrder,
    namespaces,
    workloadNames,
    kinds,
    enabled = true,
  }: UseValueReportWorkloadsListQueryArgs<SELECT>,
  organizationId: string | undefined
) => {
  const range = getRange();
  return infiniteQueryOptions({
    queryKey: getValueReportWorkloadsListQueryKey({
      clusterIds,
      key,
      useListingPrices,
      timeZone,
      range,
      organizationId,
      pageLimit,
      sortField,
      sortOrder,
      namespaces,
      workloadNames,
      kinds,
    }),
    queryFn: async ({ pageParam }) => {
      const { data } = await apiFn(
        {
          organizationId: organizationId ?? '',
          startTime: range.from,
          endTime: range.to,
          timeZone,
          useListingPrices,
          runWorkloadsValueRealizationReportRequestFilter: {
            clusterIds,
            namespaces,
            workloadNames,
            kinds,
          },
          pageCursor: pageParam,
          pageLimit: String(pageLimit),
          sortField,
          sortOrder,
        },
        { timeout: 120_000 }
      );

      return data;
    },
    initialPageParam: '',
    getNextPageParam: (lastPage) => lastPage.nextPageCursor || undefined,
    enabled: enabled && !!organizationId,
  });
};

export const useValueReportWorkloadsListQuery = <
  SELECT = UseValueReportWorkloadsListQueryData
>(
  args: UseValueReportWorkloadsListQueryArgs<SELECT>
) => {
  const { currentOrganization } = useOrganizations();

  const { data, ...query } = useInfiniteQuery(
    getValueReportWorkloadsListQueryOptions(args, currentOrganization?.id)
  );

  const flattenedData = useMemo(
    () =>
      data?.pages
        ? flatMap(data.pages, (page) => page?.items ?? [])
        : undefined,
    [data]
  );

  return {
    ...query,
    data: flattenedData,
    totalCount: data?.pages[0]?.totalCount ?? 0,
  };
};
